import { api } from "./api";
import type {
  BankStatement,
  BankTransaction,
  BankStatementFilters,
} from "../types/bankStatement";

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export const bankStatementService = {
  async uploadStatement(
    file: File,
    metadata?: {
      mes?: number;
      ano?: number;
      banco?: string;
      conta?: string;
      saldo_inicial?: number;
      saldo_final?: number;
    }
  ): Promise<BankStatement & { transactions: BankTransaction[] }> {
    const formData = new FormData();
    formData.append("pdf", file);

    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined) {
          formData.append(key, value.toString());
        }
      });
    }

    const response = await api.post<
      ApiResponse<BankStatement & { transactions: BankTransaction[] }>
    >("/bank-statements", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Failed to upload statement");
    }

    return response.data.data;
  },

  async getStatements(filters?: BankStatementFilters): Promise<{
    statements: BankStatement[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await api.get<
      ApiResponse<BankStatement[]> & {
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }
    >("/bank-statements", { params: filters });

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Failed to fetch statements");
    }

    return {
      statements: response.data.data,
      pagination: response.data.pagination!,
    };
  },

  async getStatement(
    id: number
  ): Promise<BankStatement & { transactions: BankTransaction[] }> {
    const response = await api.get<
      ApiResponse<BankStatement & { transactions: BankTransaction[] }>
    >(`/bank-statements/${id}`);

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Failed to fetch statement");
    }

    return response.data.data;
  },

  async updateTransaction(
    id: number,
    data: { categoria?: string; notas?: string }
  ): Promise<BankTransaction> {
    const response = await api.put<ApiResponse<BankTransaction>>(
      `/bank-statements/transactions/${id}`,
      data
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || "Failed to update transaction");
    }

    return response.data.data;
  },

  async deleteStatement(id: number): Promise<void> {
    const response = await api.delete<ApiResponse<void>>(
      `/bank-statements/${id}`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Failed to delete statement");
    }
  },
};
